/**
 * Unified Modal Component
 * Supports both common and admin page usage with size and footer options
 */

import { useEffect } from 'react';
import './Modal.css';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  size?: 'small' | 'medium' | 'large';
  footer?: React.ReactNode;
  desktopOnly?: boolean;
  className?: string;
  bodyClassName?: string;
}

const Modal = ({
  isOpen,
  onClose,
  children,
  title,
  size = 'medium',
  footer,
  desktopOnly = false,
  className = '',
  bodyClassName = ''
}: ModalProps) => {
  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className={`modal-overlay ${desktopOnly ? 'modal-overlay--desktop-only' : ''} ${className}`}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div className={`modal modal--${size}`} onClick={(e) => e.stopPropagation()}>
        {title && (
          <div className="modal__header">
            <h2 className="modal__title">{title}</h2>
            <button
              className="modal-close"
              onClick={onClose}
              aria-label="關閉"
            >
              ×
            </button>
          </div>
        )}
        <div className={`modal__body ${bodyClassName}`}>
          {children}
        </div>
        {footer && (
          <div className="modal__footer">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
